import { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Full IT + EN, essential strings for other languages (fallback to English)
const it = {
  nav: { manifesto: 'Manifesto', library: 'Biblioteca', help: 'Chiedi aiuto', projects: 'Progetti', transparency: 'Trasparenza', collaborators: 'Collaboratori', testimonials: 'Testimonianze', contact: 'Contatti', assistant: 'Assistente', login: 'Accedi', register: 'Registrati', dashboard: 'Area personale', logout: 'Esci', diary: 'Diario', archive: 'Archivio', admin: 'Admin' },
  hero: {
    eyebrow: 'Riparare il mondo, un principio alla volta',
    title: 'Una piattaforma universale per\nverità, giustizia e dignità.',
    subtitle: 'Tikkun aiuta le persone a comprendere i propri diritti, ottenere supporto concreto, documentare fatti e costruire una società più trasparente. Nessuna religione. Nessuna politica. Solo principi verificabili.',
    cta_help: 'Chiedi aiuto', cta_offer: 'Offri competenze', cta_discover: 'Scopri il progetto',
  },
  principles: {
    title: 'Sette principi, un fondamento', subtitle: 'Non chiediamo di credere. Chiediamo di verificare.',
    items: [
      { title: 'Dignità', body: 'Ogni persona possiede dignità inalienabile.' },
      { title: 'Uguaglianza davanti alla legge', body: 'Nessuno è al di sopra della legge.' },
      { title: 'Controllo del potere', body: 'Il potere deve essere trasparente e controllabile.' },
      { title: 'Verità', body: 'La verità viene prima delle opinioni.' },
      { title: 'Verificabilità', body: 'Ogni affermazione deve poter essere verificata.' },
      { title: 'Responsabilità', body: 'La libertà richiede responsabilità.' },
      { title: 'Riparazione', body: 'Lo scopo della giustizia è riparare il danno.' },
    ],
  },
  library: {
    title: 'Biblioteca', subtitle: 'Conoscenza verificata, opinioni distinte dai fatti.',
    search: 'Cerca articoli...', all_categories: 'Tutte le categorie', all_reliabilities: 'Ogni attendibilità',
    read: 'Leggi', min: 'min', updated: 'Aggiornato', sources: 'Fonti', tags: 'Tag', by: 'A cura di', empty: 'Nessun articolo corrisponde ai filtri.',
    summarize: 'Riassumi con AI', summary_title: 'Riassunto',
  },
  help: {
    title: 'Chiedi aiuto', subtitle: 'Compila i passi. Nessun dato è pubblico senza il tuo consenso.',
    step: 'Passo', of: 'di', next: 'Avanti', back: 'Indietro', submit: 'Invia richiesta',
    s1_title: 'Che tipo di problema riscontri?',
    s1_types: [
      { key: 'legale', label: 'Legale / Diritti' }, { key: 'salute', label: 'Salute' },
      { key: 'lavoro', label: 'Lavoro' }, { key: 'famiglia', label: 'Famiglia' },
      { key: 'psicologico', label: 'Supporto psicologico' }, { key: 'tecnologico', label: 'Tecnologico / Privacy' },
      { key: 'altro', label: 'Altro' },
    ],
    s2_title: 'Qual è la priorità?',
    priorities: [
      { key: 'low', label: 'Bassa', desc: 'Non è urgente' },
      { key: 'medium', label: 'Media', desc: 'Richiede attenzione' },
      { key: 'high', label: 'Alta', desc: 'Situazione difficile' },
      { key: 'urgent', label: 'Urgente', desc: 'Serve subito' },
    ],
    s3_title: 'Racconta la tua situazione', s3_title_label: 'Titolo breve', s3_desc_label: 'Descrizione',
    s3_desc_ph: 'Descrivi i fatti con calma, senza interpretazioni.',
    s3_loc_label: 'Località (opzionale)', s3_loc_ph: 'Città, regione',
    s4_title: 'Quale supporto ti servirebbe?',
    s4_options: [
      { key: 'legale', label: 'Consulenza legale' }, { key: 'medico', label: 'Consulenza medica' },
      { key: 'psicologico', label: 'Ascolto psicologico' }, { key: 'traduzione', label: 'Traduzione' },
      { key: 'informatico', label: 'Aiuto informatico' }, { key: 'documentale', label: 'Aiuto documentale' },
      { key: 'orientamento', label: 'Orientamento generale' },
    ],
    s5_title: 'Come possiamo ricontattarti?', s5_email_label: 'Email (opzionale)',
    s5_note: 'Se non fornisci un contatto, potrai comunque vedere lo stato della richiesta accedendo alla tua area personale.',
    success_title: 'Richiesta ricevuta',
    success_body: 'Grazie per la fiducia. Un collaboratore verificato prenderà in carico la tua richiesta.',
    new_request: 'Nuova richiesta',
  },
  manifesto: { title: 'Manifesto', subtitle: 'I fondamenti della piattaforma. Universali, verificabili, apolitici.', close: 'Il nostro impegno è che ogni parola qui scritta sia coerente con i fatti che documentiamo.' },
  reliability: {
    verified_fact: 'Fatto verificato', testimonial: 'Testimonianza', opinion: 'Opinione',
    hypothesis: 'Ipotesi', interpretation: 'Interpretazione', unverified_data: 'Dato non verificato',
  },
  auth: {
    login: 'Accedi', register: 'Crea un account', email: 'Email', password: 'Password', name: 'Nome',
    submit_login: 'Accedi', submit_register: 'Registrati', or: 'oppure', with_google: 'Continua con Google',
    no_account: 'Non hai un account?', have_account: 'Hai già un account?',
    welcome: 'Bentornato', join: 'Unisciti al progetto',
  },
  dashboard: { title: 'La tua area', hello: 'Ciao', my_requests: 'Le tue richieste di aiuto', no_requests: 'Non hai ancora inviato richieste. Puoi farlo dalla sezione "Chiedi aiuto".', status: 'Stato', quick_actions: 'Azioni rapide' },
  projects: {
    title: 'Progetti', subtitle: 'Iniziative concrete costruite insieme.',
    status: { idea: 'Idea', planning: 'In pianificazione', active: 'Attivo', completed: 'Completato', paused: 'In pausa' },
    budget: 'Budget', volunteers_needed: 'Volontari cercati', roadmap: 'Roadmap',
  },
  transparency: {
    title: 'Trasparenza', subtitle: 'Ogni euro, ogni decisione, verificabile.',
    donations: 'Donazioni', expenses: 'Spese', balance: 'Bilancio',
    users: 'Persone registrate', requests: 'Richieste ricevute', resolved: 'Risolte',
    ledger: 'Registro movimenti', changelog: 'Registro modifiche', date: 'Data', type: 'Tipo', label: 'Descrizione', amount: 'Importo',
    donation: 'Donazione', expense: 'Spesa',
  },
  collaborators: {
    title: 'Collaboratori', subtitle: 'Persone che mettono a disposizione competenze verificate.',
    offer_cta: 'Offri le tue competenze', filter: 'Filtra per competenza',
    hours_week: 'ore/settimana', verified: 'Verificato', unverified: 'Non ancora verificato',
    offer_title: 'Offri le tue competenze', bio_label: 'Bio', bio_ph: 'Descrivi la tua esperienza e come vuoi contribuire.',
    skills_label: 'Competenze', hours_label: 'Ore settimanali disponibili', credentials_label: 'Credenziali/certificazioni (opzionale)',
    email_label: 'Email di contatto (opzionale)', submit: 'Invia proposta', updated: 'Offerta aggiornata',
    skills: [
      { key: 'medicina', label: 'Medicina' }, { key: 'diritto', label: 'Diritto' },
      { key: 'informatica', label: 'Informatica' }, { key: 'grafica', label: 'Grafica' },
      { key: 'psicologia', label: 'Psicologia' }, { key: 'educazione', label: 'Educazione' },
      { key: 'volontariato', label: 'Volontariato' }, { key: 'traduzioni', label: 'Traduzioni' },
    ],
  },
  testimonials: {
    title: 'Testimonianze', subtitle: 'Storie raccontate in prima persona. Non fatti, ma esperienze.',
    add: 'Racconta la tua', body_label: 'La tua testimonianza', role_label: 'Ruolo (opzionale)', anonymous_label: 'Pubblica in forma anonima',
    submit: 'Invia', pending: 'Verrà pubblicata dopo verifica.',
    empty: 'Nessuna testimonianza pubblicata al momento.',
  },
  results: {
    title: 'Risultati', subtitle: 'Impatto misurabile, aggiornato in tempo reale.',
  },
  diary: {
    title: 'Diario personale', subtitle: 'Cifrato. Solo tu puoi leggerlo.',
    new: 'Nuova voce', new_title: 'Titolo', new_body: 'Contenuto', mood: 'Umore', tags: 'Tag (separati da virgola)',
    save: 'Salva', delete: 'Elimina', empty: 'Il tuo diario è vuoto.', privacy_note: 'Ogni voce è cifrata con AES-256 prima di essere salvata.',
  },
  archive: {
    title: 'Archivio documentale', subtitle: 'I tuoi documenti al sicuro. Cifrati. Ricercabili.',
    upload: 'Carica documento', drop: 'Trascina un file qui o clicca per selezionare',
    max_size: 'Max 5 MB', search: 'Cerca...', tags_label: 'Tag', notes_label: 'Note',
    filename: 'File', size: 'Dimensione', date: 'Data', download: 'Scarica', delete: 'Elimina',
    empty: 'Nessun documento caricato.',
  },
  contact: {
    title: 'Contatti', subtitle: 'Domande, proposte, segnalazioni. Ti rispondiamo entro 72 ore.',
    name: 'Nome', email: 'Email', subject: 'Oggetto', body: 'Messaggio', submit: 'Invia',
    success: 'Messaggio ricevuto. Grazie.',
  },
  assistant: {
    title: 'Assistente Tikkun', subtitle: 'Domande sulla biblioteca. Risposte basate solo su fonti verificate.',
    ph: 'Fai una domanda sulla biblioteca...', ask: 'Chiedi', thinking: 'Sto cercando nelle fonti...',
    sources: 'Fonti consultate', disclaimer: 'Le risposte sono generate da un modello AI a partire dagli articoli della biblioteca. Verifica sempre le fonti.',
  },
  admin: {
    title: 'Amministrazione', overview: 'Panoramica', users: 'Utenti', requests: 'Richieste', testimonials: 'Testimonianze', audit: 'Audit log',
    total: 'Totale', pending: 'In attesa', approve: 'Approva',
    set_status: 'Aggiorna stato', received: 'Ricevuta', in_progress: 'In corso', resolved: 'Risolta', closed: 'Chiusa',
  },
  footer: {
    tagline: 'Riparare il mondo con verità e dignità.',
    product: 'Prodotto', company: 'Progetto', legal: 'Legale',
    rights: 'Tutti i diritti riservati.',
  },
  common: { language: 'Lingua', theme_light: 'Chiaro', theme_dark: 'Scuro', loading: 'Caricamento...', error: 'Si è verificato un errore.', font_size: 'Testo', save: 'Salva', cancel: 'Annulla' },
};

const en = {
  nav: { manifesto: 'Manifesto', library: 'Library', help: 'Ask for help', projects: 'Projects', transparency: 'Transparency', collaborators: 'Collaborators', testimonials: 'Testimonials', contact: 'Contact', assistant: 'Assistant', login: 'Sign in', register: 'Sign up', dashboard: 'Dashboard', logout: 'Sign out', diary: 'Journal', archive: 'Archive', admin: 'Admin' },
  hero: {
    eyebrow: 'Repairing the world, one principle at a time',
    title: 'A universal platform for\ntruth, justice and dignity.',
    subtitle: 'Tikkun helps people understand their rights, get concrete support, document facts and build a more transparent society. No religion. No politics. Only verifiable principles.',
    cta_help: 'Ask for help', cta_offer: 'Offer your skills', cta_discover: 'Discover the project',
  },
  principles: {
    title: 'Seven principles, one foundation', subtitle: 'We do not ask you to believe. We ask you to verify.',
    items: [
      { title: 'Dignity', body: 'Every person holds inalienable dignity.' },
      { title: 'Equality before the law', body: 'No one is above the law.' },
      { title: 'Power under control', body: 'Power must be transparent and accountable.' },
      { title: 'Truth', body: 'Truth precedes opinions.' },
      { title: 'Verifiability', body: 'Every claim must be verifiable.' },
      { title: 'Responsibility', body: 'Freedom requires responsibility.' },
      { title: 'Repair', body: 'The purpose of justice is to repair harm.' },
    ],
  },
  library: {
    title: 'Library', subtitle: 'Verified knowledge, opinions distinct from facts.',
    search: 'Search articles...', all_categories: 'All categories', all_reliabilities: 'Any reliability',
    read: 'Read', min: 'min', updated: 'Updated', sources: 'Sources', tags: 'Tags', by: 'By', empty: 'No articles match your filters.',
    summarize: 'Summarize with AI', summary_title: 'Summary',
  },
  help: {
    title: 'Ask for help', subtitle: 'Fill in the steps. Nothing is public without your consent.',
    step: 'Step', of: 'of', next: 'Next', back: 'Back', submit: 'Submit request',
    s1_title: 'What kind of problem are you facing?',
    s1_types: [
      { key: 'legale', label: 'Legal / Rights' }, { key: 'salute', label: 'Health' },
      { key: 'lavoro', label: 'Work' }, { key: 'famiglia', label: 'Family' },
      { key: 'psicologico', label: 'Psychological support' }, { key: 'tecnologico', label: 'Technology / Privacy' },
      { key: 'altro', label: 'Other' },
    ],
    s2_title: 'What is the priority?',
    priorities: [
      { key: 'low', label: 'Low', desc: 'Not urgent' },
      { key: 'medium', label: 'Medium', desc: 'Needs attention' },
      { key: 'high', label: 'High', desc: 'Difficult situation' },
      { key: 'urgent', label: 'Urgent', desc: 'Needed now' },
    ],
    s3_title: 'Tell your story', s3_title_label: 'Short title', s3_desc_label: 'Description',
    s3_desc_ph: 'Describe the facts calmly, without interpretation.',
    s3_loc_label: 'Location (optional)', s3_loc_ph: 'City, region',
    s4_title: 'What kind of support would you need?',
    s4_options: [
      { key: 'legale', label: 'Legal counsel' }, { key: 'medico', label: 'Medical counsel' },
      { key: 'psicologico', label: 'Psychological listening' }, { key: 'traduzione', label: 'Translation' },
      { key: 'informatico', label: 'Technical help' }, { key: 'documentale', label: 'Document help' },
      { key: 'orientamento', label: 'General guidance' },
    ],
    s5_title: 'How can we reach you?', s5_email_label: 'Email (optional)',
    s5_note: 'If you do not leave contact info, you can still see the status by signing into your dashboard.',
    success_title: 'Request received',
    success_body: 'Thank you for your trust. A verified collaborator will take care of your request.',
    new_request: 'New request',
  },
  manifesto: { title: 'Manifesto', subtitle: 'The foundations of the platform. Universal, verifiable, apolitical.', close: 'Our commitment: every word here matches the facts we document.' },
  reliability: { verified_fact: 'Verified fact', testimonial: 'Testimonial', opinion: 'Opinion', hypothesis: 'Hypothesis', interpretation: 'Interpretation', unverified_data: 'Unverified data' },
  auth: {
    login: 'Sign in', register: 'Create account', email: 'Email', password: 'Password', name: 'Name',
    submit_login: 'Sign in', submit_register: 'Sign up', or: 'or', with_google: 'Continue with Google',
    no_account: "Don't have an account?", have_account: 'Already have an account?',
    welcome: 'Welcome back', join: 'Join the project',
  },
  dashboard: { title: 'Your area', hello: 'Hi', my_requests: 'Your help requests', no_requests: 'No requests yet. You can send one from "Ask for help".', status: 'Status', quick_actions: 'Quick actions' },
  projects: { title: 'Projects', subtitle: 'Concrete initiatives built together.', status: { idea: 'Idea', planning: 'Planning', active: 'Active', completed: 'Completed', paused: 'Paused' }, budget: 'Budget', volunteers_needed: 'Volunteers sought', roadmap: 'Roadmap' },
  transparency: { title: 'Transparency', subtitle: 'Every euro, every decision, verifiable.', donations: 'Donations', expenses: 'Expenses', balance: 'Balance', users: 'Registered people', requests: 'Requests received', resolved: 'Resolved', ledger: 'Ledger', changelog: 'Changelog', date: 'Date', type: 'Type', label: 'Description', amount: 'Amount', donation: 'Donation', expense: 'Expense' },
  collaborators: {
    title: 'Collaborators', subtitle: 'People offering verified skills.',
    offer_cta: 'Offer your skills', filter: 'Filter by skill', hours_week: 'hours/week',
    verified: 'Verified', unverified: 'Not yet verified',
    offer_title: 'Offer your skills', bio_label: 'Bio', bio_ph: 'Describe your experience and how you want to contribute.',
    skills_label: 'Skills', hours_label: 'Weekly hours available', credentials_label: 'Credentials (optional)',
    email_label: 'Contact email (optional)', submit: 'Submit offer', updated: 'Offer updated',
    skills: [
      { key: 'medicina', label: 'Medicine' }, { key: 'diritto', label: 'Law' },
      { key: 'informatica', label: 'IT' }, { key: 'grafica', label: 'Design' },
      { key: 'psicologia', label: 'Psychology' }, { key: 'educazione', label: 'Education' },
      { key: 'volontariato', label: 'Volunteering' }, { key: 'traduzioni', label: 'Translation' },
    ],
  },
  testimonials: {
    title: 'Testimonials', subtitle: 'First-person stories. Not facts, experiences.',
    add: 'Share yours', body_label: 'Your testimonial', role_label: 'Role (optional)', anonymous_label: 'Publish anonymously',
    submit: 'Submit', pending: 'Will be published after review.', empty: 'No testimonials yet.',
  },
  results: { title: 'Results', subtitle: 'Measurable impact, in real time.' },
  diary: {
    title: 'Personal journal', subtitle: 'Encrypted. Only you can read it.',
    new: 'New entry', new_title: 'Title', new_body: 'Content', mood: 'Mood', tags: 'Tags (comma-separated)',
    save: 'Save', delete: 'Delete', empty: 'Your journal is empty.', privacy_note: 'Every entry is AES-256 encrypted before storage.',
  },
  archive: {
    title: 'Document archive', subtitle: 'Your documents, safe. Encrypted. Searchable.',
    upload: 'Upload document', drop: 'Drop a file here or click to select', max_size: 'Max 5 MB',
    search: 'Search...', tags_label: 'Tags', notes_label: 'Notes',
    filename: 'File', size: 'Size', date: 'Date', download: 'Download', delete: 'Delete',
    empty: 'No documents yet.',
  },
  contact: { title: 'Contact', subtitle: 'Questions, proposals, reports. We reply within 72 hours.', name: 'Name', email: 'Email', subject: 'Subject', body: 'Message', submit: 'Send', success: 'Message received. Thank you.' },
  assistant: { title: 'Tikkun Assistant', subtitle: 'Questions on the library. Answers based only on verified sources.', ph: 'Ask a question about the library...', ask: 'Ask', thinking: 'Searching the sources...', sources: 'Sources consulted', disclaimer: 'Answers are AI-generated from library articles. Always verify sources.' },
  admin: { title: 'Administration', overview: 'Overview', users: 'Users', requests: 'Requests', testimonials: 'Testimonials', audit: 'Audit log', total: 'Total', pending: 'Pending', approve: 'Approve', set_status: 'Update status', received: 'Received', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' },
  footer: { tagline: 'Repair the world with truth and dignity.', product: 'Product', company: 'Project', legal: 'Legal', rights: 'All rights reserved.' },
  common: { language: 'Language', theme_light: 'Light', theme_dark: 'Dark', loading: 'Loading...', error: 'An error occurred.', font_size: 'Text', save: 'Save', cancel: 'Cancel' },
};

// Basic translations for other supported languages (nav + hero core)
const overlays = {
  he: {
    nav: { manifesto: 'מניפסט', library: 'ספרייה', help: 'בקש עזרה', projects: 'פרויקטים', transparency: 'שקיפות', collaborators: 'משתפי פעולה', testimonials: 'עדויות', contact: 'צור קשר', assistant: 'עוזר', login: 'התחבר', register: 'הרשם', dashboard: 'האזור שלי', logout: 'התנתק', diary: 'יומן', archive: 'ארכיון', admin: 'ניהול' },
    hero: {
      eyebrow: 'תיקון עולם, עיקרון אחד בכל פעם',
      title: 'פלטפורמה אוניברסלית ל-\nאמת, צדק וכבוד.',
      subtitle: 'Tikkun עוזרת לאנשים להבין את זכויותיהם, לקבל תמיכה מוחשית, לתעד עובדות ולבנות חברה שקופה יותר.',
      cta_help: 'בקש עזרה', cta_offer: 'הצע כישורים', cta_discover: 'גלה את הפרויקט',
    },
  },
  fr: {
    nav: { manifesto: 'Manifeste', library: 'Bibliothèque', help: 'Demander de l\'aide', projects: 'Projets', transparency: 'Transparence', collaborators: 'Collaborateurs', testimonials: 'Témoignages', contact: 'Contact', assistant: 'Assistant', login: 'Connexion', register: 'S\'inscrire', dashboard: 'Mon espace', logout: 'Déconnexion', diary: 'Journal', archive: 'Archives', admin: 'Admin' },
    hero: { eyebrow: 'Réparer le monde, un principe à la fois', title: 'Une plateforme universelle pour\nla vérité, la justice et la dignité.', subtitle: 'Tikkun aide à comprendre ses droits, obtenir un soutien concret, documenter les faits et construire une société plus transparente.', cta_help: 'Demander de l\'aide', cta_offer: 'Offrir des compétences', cta_discover: 'Découvrir le projet' },
  },
  es: {
    nav: { manifesto: 'Manifiesto', library: 'Biblioteca', help: 'Pedir ayuda', projects: 'Proyectos', transparency: 'Transparencia', collaborators: 'Colaboradores', testimonials: 'Testimonios', contact: 'Contacto', assistant: 'Asistente', login: 'Ingresar', register: 'Registrarse', dashboard: 'Mi área', logout: 'Salir', diary: 'Diario', archive: 'Archivo', admin: 'Admin' },
    hero: { eyebrow: 'Reparar el mundo, un principio a la vez', title: 'Una plataforma universal para\nverdad, justicia y dignidad.', subtitle: 'Tikkun ayuda a comprender los derechos, obtener apoyo concreto, documentar hechos y construir una sociedad más transparente.', cta_help: 'Pedir ayuda', cta_offer: 'Ofrecer habilidades', cta_discover: 'Descubrir el proyecto' },
  },
  de: {
    nav: { manifesto: 'Manifest', library: 'Bibliothek', help: 'Hilfe suchen', projects: 'Projekte', transparency: 'Transparenz', collaborators: 'Mitwirkende', testimonials: 'Zeugnisse', contact: 'Kontakt', assistant: 'Assistent', login: 'Anmelden', register: 'Registrieren', dashboard: 'Mein Bereich', logout: 'Abmelden', diary: 'Tagebuch', archive: 'Archiv', admin: 'Admin' },
    hero: { eyebrow: 'Die Welt reparieren, ein Prinzip nach dem anderen', title: 'Eine universelle Plattform für\nWahrheit, Gerechtigkeit und Würde.', subtitle: 'Tikkun hilft, Rechte zu verstehen, konkrete Unterstützung zu erhalten, Fakten zu dokumentieren und eine transparentere Gesellschaft aufzubauen.', cta_help: 'Hilfe suchen', cta_offer: 'Fähigkeiten anbieten', cta_discover: 'Projekt entdecken' },
  },
  ar: {
    nav: { manifesto: 'البيان', library: 'المكتبة', help: 'اطلب المساعدة', projects: 'المشاريع', transparency: 'الشفافية', collaborators: 'المتعاونون', testimonials: 'الشهادات', contact: 'اتصل', assistant: 'المساعد', login: 'دخول', register: 'تسجيل', dashboard: 'مساحتي', logout: 'خروج', diary: 'يوميات', archive: 'أرشيف', admin: 'إدارة' },
    hero: { eyebrow: 'إصلاح العالم مبدأً تلو الآخر', title: 'منصة عالمية من أجل\nالحقيقة والعدالة والكرامة.', subtitle: 'تساعد تِيكون الناس على فهم حقوقهم، والحصول على دعم ملموس، وتوثيق الوقائع، وبناء مجتمع أكثر شفافية.', cta_help: 'اطلب المساعدة', cta_offer: 'قدّم مهاراتك', cta_discover: 'اكتشف المشروع' },
  },
};

function deepMerge(base, over) {
  const out = { ...base };
  for (const k of Object.keys(over || {})) {
    if (typeof over[k] === 'object' && !Array.isArray(over[k]) && over[k] !== null) {
      out[k] = deepMerge(base[k] || {}, over[k]);
    } else {
      out[k] = over[k];
    }
  }
  return out;
}

const dict = {
  it, en,
  he: deepMerge(en, overlays.he),
  fr: deepMerge(en, overlays.fr),
  es: deepMerge(en, overlays.es),
  de: deepMerge(en, overlays.de),
  ar: deepMerge(en, overlays.ar),
};

export const SUPPORTED_LANGS = [
  { key: 'it', label: 'IT', name: 'Italiano' },
  { key: 'en', label: 'EN', name: 'English' },
  { key: 'he', label: 'HE', name: 'עברית' },
  { key: 'fr', label: 'FR', name: 'Français' },
  { key: 'es', label: 'ES', name: 'Español' },
  { key: 'de', label: 'DE', name: 'Deutsch' },
  { key: 'ar', label: 'AR', name: 'العربية' },
];

const RTL_LANGS = new Set(['he', 'ar']);

const I18nContext = createContext({ lang: 'it', t: it, setLang: () => {}, dir: 'ltr' });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('tikkun_lang') : null;
    return saved || 'it';
  });
  const dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr';
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('tikkun_lang', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);
  const value = useMemo(() => ({ lang, setLang, t: dict[lang] || dict.it, dir }), [lang, dir]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
